/**
 * Punkt 41 „Kommunikationssprache“ (Antoine, 26.09.2026): die Sprache, in der mit dem Gast
 * kommuniziert wird — gut sichtbar im Vorgangskopf und im Angebots-Builder, nicht mehr im
 * Bearbeiten-Fenster versteckt.
 *
 * - Umstellen schreibt sofort (PATCH /api/events/:id). Das Häkchen „Beim Kunden merken“ ist
 *   vorausgewählt: dann startet jeder künftige Vorgang dieses Kunden in der Sprache.
 * - Die Quelle steht daneben („aus Anfrage erkannt“, „beim Kunden gemerkt“ …), damit man sieht,
 *   woher der Wert kommt.
 * - Liegt beim Gast schon etwas (Angebot, Kostenübernahme, Rechnungen, Zahlungslinks), zeigt die
 *   Antwort des Servers die Liste; je Dokument „neu senden“. Nichts wird automatisch versendet,
 *   ein unterschriebener Vertrag bleibt unverändert gültig.
 * - `darstellung="pille"` (Vorgangsseite „Salon“, Entscheidung E2 vom 26.09.2026): eine Pille im
 *   Kopf, bei Englisch blau, ein Klick öffnet das Sprachfenster mit denselben Feldern. Der Builder
 *   behält die Zeile.
 */
import { useState } from "react";
import {
  KOMMUNIKATIONSSPRACHEN,
  QUELLE_LABEL,
  SPRACH_LABEL,
  type Kommunikationssprache,
  type SprachQuelle,
} from "@maestro/kommunikationssprache";
import { apiMutate, apiMutateMitZusatz } from "../../hooks/useApi";
import { Aufklapper, Button, Checkbox, Icon, Select, useToastWennVorhanden } from "../components";
import { ErinnerungSendenDialog } from "../ErinnerungSendenDialog";
import { PILL_CLASSES, formatDate } from "../format";

export interface VersendetesDokument {
  art: "angebot" | "kostenuebernahme" | "rechnung" | "zahlungslink";
  id: string;
  am: string;
  bezeichnung: string;
  unterschrieben?: boolean;
}

interface Warnung {
  code: string;
  documents?: VersendetesDokument[];
}

export function SprachWahl({
  eventId,
  eventNumber,
  sprache,
  quelle,
  kundeName,
  onGeaendert,
  darstellung = "zeile",
}: {
  eventId: string;
  eventNumber?: string | null;
  sprache: Kommunikationssprache;
  quelle: SprachQuelle | undefined;
  /** Name des Kunden am Vorgang; ohne Kunden kein Häkchen „merken“. */
  kundeName: string | null | undefined;
  onGeaendert: () => void;
  /** `menue` (Builder, Mockup v3): derselbe Inhalt wie im Fenster der Pille, ohne eigenen
   *  Auslöser — steht im „⋯"-Menü des Angebots-Builders. */
  darstellung?: "zeile" | "pille" | "menue";
}) {
  const [merken, setMerken] = useState(true);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [versendet, setVersendet] = useState<VersendetesDokument[] | null>(null);

  async function umstellen(neu: string) {
    if (neu === sprache) return;
    setBusy(true);
    setFehler(null);
    try {
      const r = await apiMutateMitZusatz<unknown, { warnings?: Warnung[] }>(`/api/events/${eventId}`, "PATCH", {
        offerLanguage: neu,
        spracheBeimKundenMerken: Boolean(kundeName) && merken,
      });
      const liste = r.warnings?.find((w) => w.code === "offer_language_changed_sent_documents")?.documents ?? [];
      setVersendet(liste.length > 0 ? liste : null);
      onGeaendert();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const versendetHinweis = versendet && (
    <BereitsVersendet
      eventId={eventId}
      eventNumber={eventNumber}
      dokumente={versendet}
      onSchliessen={() => setVersendet(null)}
      onGesendet={onGeaendert}
    />
  );
  const merkenFeld = kundeName && (
    <label className="flex items-start gap-2 text-[12px]">
      <Checkbox
        data-testid="sprachwahl-merken"
        checked={merken}
        onChange={(e) => setMerken(e.target.checked)}
        className="mt-0.5 shrink-0"
      />
      <span>
        Beim Kunden „{kundeName}“ merken
        {darstellung !== "zeile" && (
          <span className="block text-[11.5px] text-secondary dark:text-secondary-fixed-dim">Neue Vorgänge starten dann in dieser Sprache.</span>
        )}
      </span>
    </label>
  );

  const auswahl = (
            <div className="flex flex-col gap-3 text-on-background dark:text-inverse-on-surface">
              <div>
                <p className="text-[14px] font-bold">Sprache für den Gast</p>
                {quelle && (
                  <p data-testid="sprachwahl-quelle" className="mt-0.5 text-[12px] text-secondary dark:text-secondary-fixed-dim">
                    {QUELLE_LABEL[quelle]}
                  </p>
                )}
              </div>
              <div role="radiogroup" aria-label="Kommunikationssprache" className="flex flex-col gap-1">
                {KOMMUNIKATIONSSPRACHEN.map((s) => (
                  <button
                    key={s}
                    type="button"
                    role="radio"
                    aria-checked={s === sprache}
                    disabled={busy}
                    onClick={() => void umstellen(s)}
                    className={`flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-[13px] font-bold ${
                      s === sprache
                        ? "bg-primary-container/60 text-on-primary-container ring-[1.5px] ring-inset ring-primary dark:bg-primary/20 dark:text-[#f0b49b]"
                        : "hover:bg-surface-container-high dark:hover:bg-surface-variant/20"
                    }`}
                  >
                    <span className={`grid h-[18px] w-[26px] place-items-center rounded text-[9.5px] font-extrabold ${s === "de" ? "bg-on-background text-surface-container-lowest dark:bg-inverse-on-surface dark:text-night-card" : "bg-[#6F675A] text-white"}`}>
                      {s.toUpperCase()}
                    </span>
                    {SPRACH_LABEL[s]}
                    {s === sprache && <Icon name="check" className="ml-auto text-[18px] text-primary" />}
                  </button>
                ))}
              </div>
              {merkenFeld && <div className="border-t border-outline-variant/30 pt-3 dark:border-outline/30">{merkenFeld}</div>}
              <p className="rounded-lg bg-surface-container-low px-3 py-2.5 text-[12px] leading-relaxed text-secondary dark:bg-surface-variant/10 dark:text-secondary-fixed-dim">
                Gilt für das Angebot, Mails an den Gast, Erinnerungen, Rechnungsmails, Nachfassen und Storno. Schon Gesendetes
                bleibt in der bisherigen Sprache.
              </p>
              {fehler && <span className="text-[12px] text-error">{fehler}</span>}
              {versendetHinweis}
            </div>
  );

  if (darstellung === "menue") return <div data-testid="sprachwahl" className="px-3.5 py-2">{auswahl}</div>;

  if (darstellung === "pille") {
    return (
      <div data-testid="sprachwahl">
        <Aufklapper
          rolle="dialog"
          className="w-[340px] p-4"
          ausloeser={({ offen, umschalten }) => (
            <button
              type="button"
              onClick={umschalten}
              aria-expanded={offen}
              aria-haspopup="dialog"
              aria-label={`Kommunikationssprache: ${SPRACH_LABEL[sprache]}`}
              data-testid="sprachwahl-pille"
              className={`inline-flex items-center gap-1.5 h-9 rounded-full border px-3.5 text-[12.5px] sm:h-auto sm:px-3 sm:py-1.5 font-bold transition-shadow ${
                sprache === "de"
                  ? "border-outline-variant bg-surface-container-lowest text-on-background dark:border-outline/40 dark:bg-night-card dark:text-inverse-on-surface"
                  : `border-transparent ${PILL_CLASSES.info}`
              } ${offen ? "ring-2 ring-primary/20" : ""}`}
            >
              <Icon name="translate" className="text-[16px] opacity-80" />
              {SPRACH_LABEL[sprache]}
              <Icon name={offen ? "expand_less" : "expand_more"} className="text-[16px] opacity-70" />
            </button>
          )}
        >
          {() => auswahl}
        </Aufklapper>
      </div>
    );
  }

  return (
    <div data-testid="sprachwahl" className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
        <span className="flex items-center gap-2 font-semibold text-on-background dark:text-inverse-on-surface">
          <Icon name="translate" className="text-[18px]" /> Kommunikationssprache
        </span>
        <div className="w-36">
          <Select
            ariaLabel="Kommunikationssprache"
            value={sprache}
            disabled={busy}
            onValueChange={(v) => void umstellen(v)}
            options={KOMMUNIKATIONSSPRACHEN.map((s) => ({ value: s, label: SPRACH_LABEL[s] }))}
          />
        </div>
        {quelle && (
          <span data-testid="sprachwahl-quelle" className="text-[12px]">
            {QUELLE_LABEL[quelle]}
          </span>
        )}
        {merkenFeld}
      </div>
      <span className="text-[11px] text-secondary dark:text-secondary-fixed-dim">
        Steuert Angebot, Mails an den Gast, Erinnerungen, Rechnungsmails, Nachfassen und Storno.
      </span>
      {fehler && <span className="text-[12px] text-error">{fehler}</span>}
      {versendetHinweis}
    </div>
  );
}

function BereitsVersendet({
  eventId,
  eventNumber,
  dokumente,
  onSchliessen,
  onGesendet,
}: {
  eventId: string;
  eventNumber?: string | null;
  dokumente: VersendetesDokument[];
  onSchliessen: () => void;
  onGesendet: () => void;
}) {
  const [angebotOffen, setAngebotOffen] = useState(false);
  /** Zwei Klicks je Versand: erst „neu senden“, dann „Jetzt senden“ — eine Mail geht an den Gast. */
  const [bestaetigen, setBestaetigen] = useState<string | null>(null);
  const [gesendet, setGesendet] = useState<Record<string, true>>({});
  const [fehler, setFehler] = useState<string | null>(null);

  const toast = useToastWennVorhanden();
  async function senden(d: VersendetesDokument) {
    setFehler(null);
    try {
      if (d.art === "rechnung") await apiMutate(`/api/invoices/${d.id}/send`, "POST", {});
      if (d.art === "zahlungslink") {
        const antwort = await apiMutate<{ to?: string } | undefined>(`/api/payments/${d.id}/send-link`, "POST", {});
        toast.success(antwort?.to ? `Zahlungslink an ${antwort.to} gesendet` : "Zahlungslink gesendet");
      }
      setGesendet((g) => ({ ...g, [d.id]: true }));
      onGesendet();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setBestaetigen(null);
    }
  }

  return (
    <div
      data-testid="sprachwahl-versendet"
      className="rounded-lg border border-outline-variant bg-[#F0EAE0] p-3 text-[12px] text-[#6F675A] dark:border-white/10 dark:bg-white/[0.08] dark:text-[#b8ae9e]"
    >
      <div className="mb-2 flex items-start justify-between gap-3">
        <span className="flex items-center gap-2 font-semibold">
          <Icon name="history" className="text-[16px]" /> Bereits versendet — bleibt in der bisherigen Sprache. Neu senden?
        </span>
        <button type="button" aria-label="Hinweis schließen" onClick={onSchliessen} className="opacity-70 hover:opacity-100">
          <Icon name="close" className="text-[16px]" />
        </button>
      </div>
      <ul className="flex flex-col gap-1.5">
        {dokumente.map((d) => (
          <li key={`${d.art}-${d.id}`} data-testid={`versendet-${d.art}`} className="flex flex-wrap items-center justify-between gap-2">
            <span>
              {d.bezeichnung} · {formatDate(d.am)}
            </span>
            {d.art === "kostenuebernahme" ? (
              <span className="italic">
                {d.unterschrieben ? "unterschrieben — bleibt unverändert gültig" : "Vertrag bleibt deutsch (mit englischer Übersetzung)"}
              </span>
            ) : gesendet[d.id] ? (
              <span className="flex items-center gap-1 font-semibold">
                <Icon name="check" className="text-[14px]" /> gesendet
              </span>
            ) : d.art === "angebot" ? (
              <Button type="button" variant="secondary" className="!px-2 !py-1 !text-[12px]" onClick={() => setAngebotOffen(true)}>
                Angebot neu senden
              </Button>
            ) : bestaetigen === d.id ? (
              <Button type="button" className="!px-2 !py-1 !text-[12px]" onClick={() => void senden(d)}>
                Jetzt an den Gast senden
              </Button>
            ) : (
              <Button type="button" variant="secondary" className="!px-2 !py-1 !text-[12px]" onClick={() => setBestaetigen(d.id)}>
                neu senden
              </Button>
            )}
          </li>
        ))}
      </ul>
      {fehler && <p className="mt-2 text-error">{fehler}</p>}
      <ErinnerungSendenDialog
        open={angebotOffen}
        onClose={() => setAngebotOffen(false)}
        eventId={eventId}
        eventNumber={eventNumber}
        onSent={() => {
          setAngebotOffen(false);
          setGesendet((g) => ({ ...g, [eventId]: true }));
          onGesendet();
        }}
      />
    </div>
  );
}
